if (!customElements.get("shoppable-video")) {
  class ShoppableVideo extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.thumbsSwiper = null;
      this.mainSwiper = null;
      this.modal = null;
      this._destroyed = false;
      this._boundInit = this.initWhenReady.bind(this);
      this._onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this._destroyed = false;
      this._abort = new AbortController();
      this.prevButton = this.querySelector(".shoppable-video__nav--prev");
      this.nextButton = this.querySelector(".shoppable-video__nav--next");
      this.cards = this.querySelectorAll(".shoppable-video__card");
      this.labelPlay = this.dataset.labelPlay || "";
      this.labelPause = this.dataset.labelPause || "";
      this.initWhenReady();
      this.bindVideos();
      this.bindModal();
      document.addEventListener("shopify:block:select", this._onBlockSelect);
    }

    disconnectedCallback() {
      this._destroyed = true;
      if (this._abort) {
        this._abort.abort();
      }
      if (this._modalObserver) {
        this._modalObserver.disconnect();
        this._modalObserver = null;
      }
      document.removeEventListener("DOMContentLoaded", this._boundInit);
      document.removeEventListener("shopify:block:select", this._onBlockSelect);
      this.destroy();
      if (this.modal && this.modal.parentNode === document.body) {
        if (typeof this.modal.hide === "function") {
          this.modal.hide();
        }
        this.modal.remove();
      }
    }

    getModal() {
      if (this.modal && this.modal.isConnected) {
        return this.modal;
      }
      const selector = this.dataset.modal;
      this.modal = selector ? document.querySelector(selector) : null;
      return this.modal;
    }

    initWhenReady() {
      if (this._destroyed) {
        return;
      }
      if (typeof Swiper !== "undefined") {
        this.initSwiper();
        return;
      }
      document.addEventListener("DOMContentLoaded", this._boundInit, { once: true });
    }

    initSwiper() {
      if (this.swiper || this._destroyed || typeof Swiper === "undefined") {
        return;
      }

      const container = this.querySelector(".shoppable-video__swiper");
      if (!container) {
        return;
      }

      this.swiper = new Swiper(container, {
        slidesPerView: "auto",
        spaceBetween: 8,
        loop: false,
        watchOverflow: true,
        grabCursor: true,
        observer: true,
        observeParents: true,
        preventClicks: false,
        preventClicksPropagation: false,
        touchStartPreventDefault: false,
        rtl: document.dir === "rtl",
        navigation: {
          prevEl: this.prevButton,
          nextEl: this.nextButton,
        },
      });
    }

    initModalSwipers() {
      const modal = this.getModal();
      if (
        !modal ||
        this.mainSwiper ||
        this._destroyed ||
        typeof Swiper === "undefined"
      ) {
        return;
      }

      const thumbsEl = modal.querySelector(".shoppable-video-modal__thumbs");
      const mainEl = modal.querySelector(".shoppable-video-modal__main");
      if (!mainEl) {
        return;
      }

      if (thumbsEl) {
        this.thumbsSwiper = new Swiper(thumbsEl, {
          slidesPerView: "auto",
          spaceBetween: 4,
          watchSlidesProgress: true,
          watchOverflow: true,
          freeMode: true,
          rtl: document.dir === "rtl",
        });
      }

      this.mainSwiper = new Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 8,
        loop: false,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        preventClicks: false,
        preventClicksPropagation: false,
        touchStartPreventDefault: false,
        rtl: document.dir === "rtl",
        navigation: {
          prevEl: modal.querySelector(".shoppable-video-modal__nav--prev"),
          nextEl: modal.querySelector(".shoppable-video-modal__nav--next"),
        },
        thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
        on: {
          slideChange: () => this.playModalSlide(),
        },
      });
    }

    destroy() {
      if (this.swiper && typeof this.swiper.destroy === "function") {
        this.swiper.destroy(true, true);
      }
      if (this.mainSwiper && typeof this.mainSwiper.destroy === "function") {
        this.mainSwiper.destroy(true, true);
      }
      if (this.thumbsSwiper && typeof this.thumbsSwiper.destroy === "function") {
        this.thumbsSwiper.destroy(true, true);
      }
      this.swiper = null;
      this.mainSwiper = null;
      this.thumbsSwiper = null;
    }

    bindVideos() {
      const signal = this._abort.signal;
      this.cards.forEach((card) => {
        const playBtn = card.querySelector(".shoppable-video__play");
        const chip = card.querySelector(".shoppable-video__chip");
        const video = card.querySelector("video");
        let startX = 0;
        let startY = 0;

        card.addEventListener(
          "pointerenter",
          (event) => {
            if (event.pointerType === "mouse") {
              this.playCard(card);
            }
          },
          { signal },
        );

        card.addEventListener(
          "pointerleave",
          (event) => {
            if (event.pointerType === "mouse") {
              this.pauseCard(card);
            }
          },
          { signal },
        );

        card.addEventListener(
          "pointerdown",
          (event) => {
            startX = event.clientX;
            startY = event.clientY;
          },
          { signal },
        );

        card.addEventListener(
          "click",
          (event) => {
            if (event.target.closest(".shoppable-video__chip")) {
              return;
            }
            if (Math.abs(event.clientX - startX) > 8 || Math.abs(event.clientY - startY) > 8) {
              return;
            }
            event.preventDefault();
            this.openModal(parseInt(card.dataset.index, 10) || 0, playBtn || card);
          },
          { signal },
        );

        if (playBtn) {
          playBtn.addEventListener(
            "click",
            (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.openModal(parseInt(card.dataset.index, 10) || 0, playBtn);
            },
            { signal },
          );
        }

        if (chip) {
          chip.addEventListener(
            "click",
            (event) => {
              event.stopPropagation();
            },
            { signal },
          );
        }

        if (video) {
          video.addEventListener("play", () => this.setPlaying(card, true), {
            signal,
          });
          video.addEventListener("pause", () => this.setPlaying(card, false), {
            signal,
          });
        }
      });
    }

    bindModal() {
      if (this._modalObserver) {
        return;
      }
      const modal = this.getModal();
      if (!modal) {
        return;
      }

      this._modalObserver = new MutationObserver(() => {
        if (!modal.hasAttribute("open")) {
          this.pauseModalVideos();
        }
      });
      this._modalObserver.observe(modal, {
        attributes: true,
        attributeFilter: ["open"],
      });
    }

    openModal(index, opener) {
      const modal = this.getModal();
      if (!modal || typeof modal.show !== "function") {
        return;
      }

      this.cards.forEach((card) => this.pauseCard(card));
      this.bindModal();
      this.initModalSwipers();
      modal.show(opener);

      requestAnimationFrame(() => {
        if (this.mainSwiper) {
          this.mainSwiper.slideTo(index || 0, 0);
        }
        this.playModalSlide();
      });
    }

    playModalSlide() {
      const modal = this.getModal();
      if (!modal || !this.mainSwiper) {
        return;
      }

      const slides = modal.querySelectorAll(
        ".shoppable-video-modal__main .swiper-slide",
      );
      const activeIndex = this.mainSwiper.activeIndex;

      slides.forEach((slide, index) => {
        const video = slide.querySelector("video");
        const isActive = index === activeIndex;
        slide.classList.toggle("is-playing", isActive);
        if (!video) {
          return;
        }
        if (isActive) {
          video.muted = false;
          const playPromise = video.play();
          if (playPromise) {
            playPromise.catch(() => {
              video.muted = true;
              video.play().catch(() => {});
            });
          }
        } else {
          video.pause();
        }
      });
    }

    pauseModalVideos() {
      const modal = this.getModal();
      if (!modal) {
        return;
      }
      modal.querySelectorAll("video").forEach((video) => {
        video.pause();
      });
      modal
        .querySelectorAll(".shoppable-video-modal__main .swiper-slide")
        .forEach((slide) => {
          slide.classList.remove("is-playing");
        });
    }

    pauseOthers(exceptCard) {
      this.cards.forEach((card) => {
        if (card !== exceptCard) {
          this.pauseCard(card);
        }
      });
    }

    playCard(card) {
      const video = card.querySelector("video");
      if (!video) {
        return;
      }
      this.pauseOthers(card);
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {});
      }
    }

    pauseCard(card) {
      const video = card.querySelector("video");
      if (!video) {
        return;
      }
      video.pause();
    }

    setPlaying(card, playing) {
      const playBtn = card.querySelector(".shoppable-video__play");
      const playIcon = card.querySelector(".shoppable-video__icon--play");
      const pauseIcon = card.querySelector(".shoppable-video__icon--pause");

      card.classList.toggle("is-playing", playing);

      if (playBtn) {
        playBtn.setAttribute("aria-pressed", playing ? "true" : "false");
        playBtn.setAttribute(
          "aria-label",
          playing ? this.labelPause : this.labelPlay,
        );
      }
      if (playIcon) {
        playIcon.hidden = playing;
      }
      if (pauseIcon) {
        pauseIcon.hidden = !playing;
      }
    }

    onBlockSelect(event) {
      if (!this.swiper || !this.contains(event.target)) {
        return;
      }
      const slide = event.target.closest(".shoppable-video__swiper .swiper-slide");
      if (!slide) {
        return;
      }
      const slides = Array.from(
        this.querySelectorAll(".shoppable-video__swiper .swiper-slide"),
      );
      const index = slides.indexOf(slide);
      if (index > -1) {
        this.swiper.slideTo(index);
      }
    }
  }

  customElements.define("shoppable-video", ShoppableVideo);
}
